/**
 * useDecisionUnit — 10I
 *
 * Fetches and returns the resolved DecisionUnit for a given context.
 * Server-authoritative — never computed from client-side state alone.
 *
 * Delegates stage status to useContextStageStatus rather than re-fetching.
 * The DecisionUnit returned here augments that with full provenance and
 * Frame/Decision state from across the system.
 *
 * Canon reference: DOCS_V4/00-canon/10I-cursor-decision-unit-runtime.md
 */

import { useState, useEffect, useCallback } from 'react';
import { resolveDecisionUnit } from '../../lib/decisionUnitResolver';
import type { DecisionUnit } from '../../types/prioritization/decisionUnit';
import { useContextsStore } from '../../stores/prioritization/contextsStore';

export interface UseDecisionUnitResult {
  decisionUnit: DecisionUnit | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDecisionUnit(
  contextId: string | null | undefined,
  /** The track the caller is looking at; a cycle context has several. */
  trackId?: string | null
): UseDecisionUnitResult {
  const contexts = useContextsStore((s) => s.contexts);

  const [decisionUnit, setDecisionUnit] = useState<DecisionUnit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async () => {
    if (!contextId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Derive workspace ID from the cached context if available
      const cachedContext = contexts[contextId];
      const workspaceId = cachedContext?.workspace_id ?? '';

      const unit = await resolveDecisionUnit(contextId, workspaceId, { trackId });
      if (!unit) {
        setError('Could not resolve Decision Unit for this context');
      } else {
        setDecisionUnit(unit);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error resolving Decision Unit');
    } finally {
      setIsLoading(false);
    }
  }, [contextId, trackId, contexts]);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  return { decisionUnit, isLoading, error, refresh: resolve };
}
